import {
  getBlockchainFromAddress,
  getRawAddress,
  joinAddress,
} from "@anaconda/core/blockchain/address"
import {
  getCollectionIdBySlug,
  getCollectionSlug,
  getCollectionSlugKey,
  isCollectionSlug,
} from "@anaconda/core/nft-collection/slugs"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { toCollectionId } from "@anaconda/core/nft-collection/utils"

/**
 * Launched collections' slugs, read from the BFF (`launched-collection-slugs.ts`
 * on the server, `LaunchedCollectionSlugs` in the browser). The static map in
 * core wins: its slugs are permanent, these can change.
 */
const launchedSlugByKey = new Map<string, string>()
const launchedIdBySlug = new Map<string, CollectionId>()

/** Replaces the launched slugs this module knows. */
export function setLaunchedCollectionSlugs(
  launches: ReadonlyArray<{ collectionId: string; slug?: string }>,
): void {
  launchedSlugByKey.clear()
  launchedIdBySlug.clear()
  for (const { collectionId, slug } of launches) {
    if (!slug || !isCollectionSlug(slug)) continue
    try {
      const blockchain = getBlockchainFromAddress(collectionId)
      // A collection in the static map keeps its slug there, and a static
      // slug names its own collection
      if (
        getCollectionSlug(collectionId) ||
        getCollectionIdBySlug(blockchain, slug)
      ) {
        continue
      }
      launchedSlugByKey.set(getCollectionSlugKey(collectionId), slug)
      launchedIdBySlug.set(
        joinAddress(blockchain, slug),
        toCollectionId(collectionId),
      )
    } catch {
      // Not a `blockchain:address` id
    }
  }
}

function findSlug(
  collectionId: string,
): { slug: string; permanent: boolean } | undefined {
  const fixed = getCollectionSlug(collectionId)
  if (fixed) return { slug: fixed, permanent: true }
  const launched = launchedSlugByKey.get(getCollectionSlugKey(collectionId))
  return launched ? { slug: launched, permanent: false } : undefined
}

function findCollectionBySlug(
  blockchain: string,
  slug: string,
): { collectionId: CollectionId; permanent: boolean } | undefined {
  const fixed = getCollectionIdBySlug(blockchain, slug)
  if (fixed) return { collectionId: fixed, permanent: true }
  const launched = launchedIdBySlug.get(
    joinAddress(blockchain.toUpperCase(), slug),
  )
  return launched ? { collectionId: launched, permanent: false } : undefined
}

/** Whether a slug names a collection on a chain, statically or as a launch. */
export function isKnownCollectionSlug(blockchain: string, slug: string) {
  return findCollectionBySlug(blockchain, slug) !== undefined
}

/** The `[id]` segment of a collection URL: its slug, else its address. */
export function getCollectionPathSegment(collectionId: CollectionId): string {
  return findSlug(collectionId)?.slug ?? getRawAddress(collectionId)
}

/**
 * The slug URL of a collection page reached by its address, or by its slug in
 * another case; `null` when there is none or `pathname` already uses it.
 * Subpages and the chain segment are kept. `permanent` only for a static
 * slug: a launch's can change.
 */
export function getCanonicalCollectionPath(
  pathname: string,
): { path: string; permanent: boolean } | null {
  const [blockchain, section, segment, ...rest] = pathname
    .split("/")
    .filter(Boolean)

  if (section !== "collections" || !segment) {
    return null
  }

  const lowercase = segment.toLowerCase()
  const bySlug = findCollectionBySlug(blockchain, lowercase)
  const target = bySlug
    ? { slug: lowercase, permanent: bySlug.permanent }
    : findSlug(joinAddress(blockchain.toUpperCase(), segment))

  if (!target || target.slug === segment) {
    return null
  }

  return {
    path: `/${[blockchain, section, target.slug, ...rest].join("/")}`,
    permanent: target.permanent,
  }
}

/** The collection a URL's `[id]` segment names, by slug or by address. */
export function resolveCollectionIdFromSegment(
  blockchain: string,
  segment: string,
): CollectionId {
  return (
    findCollectionBySlug(blockchain, segment)?.collectionId ??
    toCollectionId(joinAddress(blockchain, segment))
  )
}
